/**
 * Original implementation from Material UI library with some modifications
 * See [useAutocomplete](https://github.com/mui-org/material-ui/blob/next/packages/material-ui/src/useAutocomplete/useAutocomplete.js)
 * all available autocomplete option
 */

/* eslint-disable no-constant-condition, no-console */
import * as React from 'react';
import {
  unstable_useControlled as useControlled,
  unstable_useEventCallback as useEventCallback,
  unstable_useId as useId,
} from '@mui/utils';

// https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript
// Give up on IE 11 support for this feature
function stripDiacritics(string: string) {
  return typeof string.normalize !== 'undefined'
    ? string.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    : string;
}

type FilterState<T> = {
  inputValue: string;
  getOptionLabel: (option: T) => string;
  freeSolo: boolean;
};

type FilterConfig<T> = {
  ignoreAccents?: boolean;
  ignoreCase?: boolean;
  limit?: number;
  matchFrom?: 'any' | 'start';
  stringify?: (option: T) => string;
  trim?: boolean;
};

type AutocompleteEvent = React.SyntheticEvent | Event;
type ChangeReason = 'select-option' | 'remove-option' | 'clear' | 'blur';
type CloseReason = ChangeReason | 'toggleInput';
type AutocompleteProps<T> = {
  options: T[];
  value?: T[];
  defaultValue?: T[];
  inputValue?: string;
  open?: boolean;
  id?: string;
  componentName?: string;
  autoComplete?: boolean;
  autoHighlight?: boolean;
  autoSelect?: boolean;
  blurOnSelect?: boolean | 'touch' | 'mouse';
  clearOnBlur?: boolean;
  debug?: boolean;
  disableCloseOnSelect?: boolean;
  filterOptions?: (options: T[], state: FilterState<T>) => T[];
  filterSelectedOptions?: boolean;
  freeSolo?: boolean;
  getOptionDisabled?: (option: T) => boolean;
  getOptionLabel?: (option: T) => string;
  getOptionSelected?: (option: T, value: T) => boolean;
  groupBy?: (option: T) => string;
  multiple?: boolean;
  onChange?: (event: AutocompleteEvent, value: T[], reason: ChangeReason, details?: { option: T }) => void;
  onClose?: (event: AutocompleteEvent, reason: CloseReason) => void;
  onHighlightChange?: (event: AutocompleteEvent, option: T | null, reason: string) => void;
  onInputChange?: (event: AutocompleteEvent, value: string, reason: 'reset' | 'clear' | 'input') => void;
  onOpen?: (event: AutocompleteEvent) => void;
  openOnFocus?: boolean;
  selectOnFocus?: boolean;
};

export function createFilterOptions<T>(config: FilterConfig<T> = {}) {
  const {
    ignoreAccents = true,
    ignoreCase = true,
    limit,
    matchFrom = 'any',
    stringify,
    trim = false,
  } = config;

  return (options: T[], { inputValue, getOptionLabel, freeSolo }: FilterState<T>) => {
    if (freeSolo) return options;
    let input = trim ? inputValue.trim() : inputValue;
    if (ignoreCase) {
      input = input.toLowerCase();
    }
    if (ignoreAccents) {
      input = stripDiacritics(input);
    }

    const filteredOptions = options.filter((option) => {
      let candidate = (stringify || getOptionLabel)(option);
      if (ignoreCase) {
        candidate = candidate.toLowerCase();
      }
      if (ignoreAccents) {
        candidate = stripDiacritics(candidate);
      }

      return matchFrom === 'start'
        ? candidate.indexOf(input) === 0
        : candidate.indexOf(input) > -1;
    });

    return typeof limit === 'number'
      ? filteredOptions.slice(0, limit)
      : filteredOptions;
  };
}

// To replace with .findIndex() once we stop IE 11 support.
function findIndex<T>(array: T[], comp: (value: T) => boolean) {
  for (let i = 0; i < array.length; i += 1) {
    if (comp(array[i])) {
      return i;
    }
  }

  return -1;
}

const isProduction = (import.meta as ImportMeta & { env: { VITE_NODE_ENV?: string } }).env.VITE_NODE_ENV === 'production';

function setRef<T>(ref: React.MutableRefObject<T | null> | ((value: T | null) => void), node: T | null) {
  if (typeof ref === 'function') {
    ref(node);
  } else if (ref) {
    // eslint-disable-next-line no-param-reassign
    ref.current = node;
  }
}

export default function useAutocomplete<T>(props: AutocompleteProps<T>) {
  const {
    autoComplete = false,
    autoHighlight = false,
    autoSelect = false,
    blurOnSelect = false,
    clearOnBlur = !props.freeSolo,
    componentName = 'useAutocomplete',
    debug = false,
    defaultValue = [],
    disableCloseOnSelect = false,
    filterOptions = createFilterOptions<T>(),
    filterSelectedOptions = false,
    freeSolo = false,
    getOptionDisabled,
    getOptionLabel: getOptionLabelProp = (option: T) => String(option),
    getOptionSelected = (option: T, value: T) => option === value,
    groupBy,
    id: idProp,
    inputValue: inputValueProp,
    multiple = false,
    onChange,
    onClose,
    onHighlightChange,
    onInputChange,
    onOpen,
    open: openProp,
    openOnFocus = false,
    options,
    selectOnFocus = !props.freeSolo,
    value: valueProp,
  } = props;

  const id = useId(idProp);

  let getOptionLabel = getOptionLabelProp;

  if (!isProduction) {
    getOptionLabel = (option: T) => {
      const optionLabel = getOptionLabelProp(option);
      if (typeof optionLabel !== 'string') {
        const erroneousReturn =
          optionLabel === undefined
            ? 'undefined'
            : `${typeof optionLabel} (${optionLabel})`;
        console.error(
          `Material-UI: The \`getOptionLabel\` method of ${componentName} returned ${erroneousReturn} instead of a string for ${JSON.stringify(
            option,
          )}.`,
        );
      }
      return optionLabel;
    };
  }

  const ignoreFocus = React.useRef(false);
  const firstFocus = React.useRef(true);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listboxRef = React.useRef<HTMLElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const defaultHighlighted = autoHighlight ? 0 : -1;
  const highlightedIndexRef = React.useRef(defaultHighlighted);

  const [value, setValue] = useControlled<T[]>({
    controlled: valueProp,
    default: defaultValue,
    name: componentName,
  });
  const [inputValue, setInputValue] = useControlled<string>({
    controlled: inputValueProp,
    default: '',
    name: componentName,
    state: 'inputValue',
  });

  const resetInputValue = useEventCallback((event: AutocompleteEvent, newValue: T[]) => {
    let newInputValue = '';

    if (!multiple && newValue !== null) {
      const optionLabel = getOptionLabel(newValue[0]);
      newInputValue = typeof optionLabel === 'string' ? optionLabel : '';
    }

    if (inputValue === newInputValue) {
      return;
    }

    setInputValue(newInputValue);

    if (onInputChange) {
      onInputChange(event, newInputValue, 'reset');
    }
  });

  const [open, setOpenState] = useControlled<boolean>({
    controlled: openProp,
    default: false,
    name: componentName,
    state: 'open',
  });

  const popupOpen = open;

  const filteredOptions = popupOpen
    ? filterOptions(
        options.filter((option) => {
          if (
            filterSelectedOptions &&
            value.some(
              (value2) => value2 !== null && getOptionSelected(option, value2),
            )
          ) {
            return false;
          }
          return true;
        }),
        // we use the empty string to manipulate `filterOptions` to not filter any options
        // i.e. the filter predicate always returns true
        { inputValue, getOptionLabel, freeSolo },
      )
    : [];

  if (!isProduction) {
    if (value !== null && !freeSolo && options.length > 0) {
      const missingValue = value.filter(
        (value2) =>
          !options.some((option) => getOptionSelected(option, value2)),
      );

      if (missingValue.length > 0) {
        console.warn(
          [
            `Material-UI: The value provided to ${componentName} is invalid.`,
            `None of the options match with \`${
              missingValue.length > 1
                ? JSON.stringify(missingValue)
                : JSON.stringify(missingValue[0])
            }\`.`,
            'You can use the `getOptionSelected` prop to customize the equality test.',
          ].join('\n'),
        );
      }
    }
  }

  const setHighlightedIndex = useEventCallback(
    ({ event, index, reason = 'auto' }: { event: AutocompleteEvent; index: number; reason?: string }) => {
      highlightedIndexRef.current = index;

      if (inputRef.current) {
        // does the index exist?
        if (index === -1) {
          inputRef.current.removeAttribute('aria-activedescendant');
        } else {
          inputRef.current.setAttribute(
            'aria-activedescendant',
            `${id}-option-${index}`,
          );
        }
      }

      if (onHighlightChange) {
        onHighlightChange(
          event,
          index === -1 ? null : filteredOptions[index],
          reason,
        );
      }

      if (!listboxRef.current?.parentElement) {
        return;
      }

      const prev = listboxRef.current.querySelector('[data-focus]');
      if (prev) {
        prev.removeAttribute('data-focus');
      }

      const listboxNode =
        listboxRef.current.parentElement.querySelector('[role="listbox"]');

      // "No results"
      if (!listboxNode) {
        return;
      }

      if (index === -1) {
        listboxNode.scrollTop = 0;
        return;
      }

      const option = listboxRef.current.querySelector<HTMLElement>(
        `[data-option-index="${index}"]`,
      );

      if (!option) {
        return;
      }

      option.setAttribute('data-focus', 'true');

      // Scroll active descendant into view.
      // Logic copied from https://www.w3.org/TR/wai-aria-practices/examples/listbox/js/listbox.js
      //
      // Consider this API instead once it has a better browser support:
      // .scrollIntoView({ scrollMode: 'if-needed', block: 'nearest' });
      if (
        listboxNode.scrollHeight > listboxNode.clientHeight &&
        reason !== 'mouse'
      ) {
        const element = option;

        const scrollBottom = listboxNode.clientHeight + listboxNode.scrollTop;
        const elementBottom = element.offsetTop + element.offsetHeight;
        if (elementBottom > scrollBottom) {
          listboxNode.scrollTop = elementBottom - listboxNode.clientHeight;
        } else if (
          element.offsetTop - element.offsetHeight * (groupBy ? 1.3 : 0) <
          listboxNode.scrollTop
        ) {
          listboxNode.scrollTop =
            element.offsetTop - element.offsetHeight * (groupBy ? 1.3 : 0);
        }
      }
    },
  );

  const handleListboxRef = useEventCallback((node: HTMLElement | null) => {
    setRef(listboxRef, node);
  });

  const handleOpen = (event: AutocompleteEvent) => {
    if (open) {
      return;
    }

    setOpenState(true);

    if (onOpen) {
      onOpen(event);
    }
  };

  const handleClose = (event: AutocompleteEvent, reason: CloseReason) => {
    if (!open) {
      return;
    }

    setOpenState(false);

    if (onClose) {
      onClose(event, reason);
    }
  };

  const handleValue = (event: AutocompleteEvent, newValue: T[], reason: ChangeReason, details?: { option: T }) => {
    if (value === newValue) {
      return;
    }

    if (onChange) {
      onChange(event, newValue, reason, details);
    }

    setValue(newValue);
  };

  const isTouch = React.useRef(false);

  const selectNewValue = (
    event: AutocompleteEvent,
    option: T,
    reasonProp: ChangeReason = 'select-option',
    origin = 'options',
  ) => {
    let reason = reasonProp;

    const newValue = Array.isArray(value) ? value.slice() : [];

    if (multiple) {
      if (!isProduction) {
        const matches = newValue.filter((val) =>
          getOptionSelected(option, val),
        );

        if (matches.length > 1) {
          console.error(
            [
              `Material-UI: The \`getOptionSelected\` method of ${componentName} do not handle the arguments correctly.`,
              `The component expects a single value to match a given option but found ${matches.length} matches.`,
            ].join('\n'),
          );
        }
      }
    }

    const itemIndex = findIndex(newValue, (valueItem) =>
      getOptionSelected(option, valueItem),
    );

    if (itemIndex === -1) {
      if (!multiple) {
        newValue.pop();
      }
      newValue.push(option);
    } else if (origin !== 'freeSolo') {
      newValue.splice(itemIndex, 1);
      reason = 'remove-option';
    }

    handleValue(event, newValue, reason, { option });
    if (!disableCloseOnSelect) {
      handleClose(event, reason);
    }

    if (
      blurOnSelect === true ||
      (blurOnSelect === 'touch' && isTouch.current) ||
      (blurOnSelect === 'mouse' && !isTouch.current)
    ) {
      if (inputRef.current) inputRef.current.blur();
    }
  };

  const handleClear = (event: React.MouseEvent<HTMLElement>) => {
    ignoreFocus.current = true;
    setInputValue('');

    if (onInputChange) {
      onInputChange(event, '', 'clear');
    }

    handleValue(event, [], 'clear');
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (openOnFocus && !ignoreFocus.current) {
      handleOpen(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    // Ignore the event when using the scrollbar with IE 11
    if (
      listboxRef.current !== null &&
      document.activeElement === listboxRef.current.parentElement
    ) {
      if (inputRef.current) inputRef.current.focus();
      return;
    }

    firstFocus.current = true;
    ignoreFocus.current = false;

    if (debug && inputValue !== '') {
      return;
    }

    if (autoSelect && highlightedIndexRef.current !== -1 && popupOpen) {
      selectNewValue(
        event,
        filteredOptions[highlightedIndexRef.current],
        'blur',
      );
    } else if (autoSelect && freeSolo && inputValue !== '') {
      selectNewValue(event, inputValue as T, 'blur', 'freeSolo');
    } else if (clearOnBlur) {
      resetInputValue(event, value);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (inputValue !== newValue) {
      setInputValue(newValue);

      if (onInputChange) {
        onInputChange(event, newValue, 'input');
      }
    }

    if (newValue !== '') {
      handleOpen(event);
    }
  };

  const handleOptionMouseOver = (event: React.MouseEvent<HTMLElement>) => {
    setHighlightedIndex({
      event,
      index: Number(event.currentTarget.getAttribute('data-option-index')),
      reason: 'mouse',
    });
  };

  const handleOptionTouchStart = () => {
    isTouch.current = true;
  };

  const handleOptionClick = (event: React.MouseEvent<HTMLElement>) => {
    const index = Number(event.currentTarget.getAttribute('data-option-index'));
    selectNewValue(event, filteredOptions[index], 'select-option');

    isTouch.current = false;
  };

  const handleTagDelete = (index: number) => (event: React.MouseEvent<HTMLElement>) => {
    const newValue = value.slice();
    newValue.splice(index, 1);
    handleValue(event, newValue, 'remove-option', {
      option: value[index],
    });
  };

  const handlePopupIndicator = (event: React.MouseEvent<HTMLElement>) => {
    if (open) {
      handleClose(event, 'toggleInput');
    } else {
      handleOpen(event);
    }
  };

  // Prevent input blur when interacting with the combobox
  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).getAttribute('id') !== id) {
      event.preventDefault();
    }
  };

  // Focus the input when interacting with the combobox
  const handleClick = () => {
    if (inputRef.current) inputRef.current.focus();

    if (
      selectOnFocus &&
      firstFocus.current &&
      inputRef.current && inputRef.current.selectionEnd === inputRef.current.selectionStart
    ) {
      inputRef.current.select();
    }

    firstFocus.current = false;
  };

  const handleInputMouseDown = (event: React.MouseEvent<HTMLInputElement>) => {
    if (inputValue === '' || !open) {
      handlePopupIndicator(event);
    }
  };

  let dirty = freeSolo && inputValue.length > 0;
  dirty = dirty || (multiple ? value.length > 0 : value !== null);

  let groupedOptions: T[] | { key: number; index: number; group: string; options: T[] }[] = filteredOptions;
  if (groupBy) {
    // used to keep track of key and indexes in the result array
    const indexBy = new Map();
    let warn = false;

    groupedOptions = filteredOptions.reduce<{ key: number; index: number; group: string; options: T[] }[]>((acc, option, index) => {
      const group = groupBy(option);

      if (acc.length > 0 && acc[acc.length - 1].group === group) {
        acc[acc.length - 1].options.push(option);
      } else {
        if (!isProduction) {
          if (indexBy.get(group) && !warn) {
            console.warn(
              `Material-UI: The options provided combined with the \`groupBy\` method of ${componentName} returns duplicated headers.`,
              'You can solve the issue by sorting the options with the output of `groupBy`.',
            );
            warn = true;
          }
          indexBy.set(group, true);
        }

        acc.push({
          key: index,
          index,
          group,
          options: [option],
        });
      }

      return acc;
    }, []);
  }

  return {
    getRootProps: (other: React.HTMLAttributes<HTMLElement> = {}) => ({
      'aria-owns': popupOpen ? `${id}-popup` : null,
      role: 'combobox',
      'aria-expanded': popupOpen,
      ...other,
      onMouseDown: handleMouseDown,
      onClick: handleClick,
    }),
    getInputLabelProps: () => ({
      id: `${id}-label`,
      htmlFor: id,
    }),
    getInputProps: () => ({
      id,
      value: inputValue,
      onBlur: handleBlur,
      onFocus: handleFocus,
      onChange: handleInputChange,
      onMouseDown: handleInputMouseDown,
      // if open then this is handled imperativeley so don't let react override
      // only have an opinion about this when closed
      'aria-activedescendant': popupOpen ? '' : null,
      'aria-autocomplete': autoComplete ? 'both' : 'list',
      'aria-controls': popupOpen ? `${id}-popup` : null,
      // Disable browser's suggestion that might overlap with the popup.
      // Handle autocomplete but not autofill.
      autoComplete: 'off',
      ref: inputRef,
      autoCapitalize: 'none',
      spellCheck: 'false',
    }),
    getClearProps: () => ({
      tabIndex: -1,
      onClick: handleClear,
    }),
    getPopupIndicatorProps: () => ({
      tabIndex: -1,
      onClick: handlePopupIndicator,
    }),
      getTagProps: ({ index }: { index: number }) => ({
      key: index,
      'data-tag-index': index,
      tabIndex: -1,
      onDelete: handleTagDelete(index),
    }),
    getListboxProps: () => ({
      role: 'listbox',
      id: `${id}-popup`,
      'aria-labelledby': `${id}-label`,
      ref: handleListboxRef,
      onMouseDown: (event: React.MouseEvent<HTMLElement>) => {
        // Prevent blur
        event.preventDefault();
      },
    }),
    getOptionProps: ({ index, option }: { index: number; option: T }) => {
      const selected = value.some(
        (value2) => value2 != null && getOptionSelected(option, value2),
      );
      const disabled = getOptionDisabled ? getOptionDisabled(option) : false;

      return {
        key: index,
        tabIndex: -1,
        role: 'option',
        id: `${id}-option-${index}`,
        onMouseOver: handleOptionMouseOver,
        onClick: handleOptionClick,
        onTouchStart: handleOptionTouchStart,
        'data-option-index': index,
        'aria-disabled': disabled,
        'aria-selected': selected,
      };
    },
    id,
    inputValue,
    value,
    dirty,
    popupOpen,
    anchorEl,
    setAnchorEl,
    groupedOptions,
  };
}
